import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { useLanguage } from '../../context/LanguageContext';
import { useSeason } from '../../context/SeasonContext';
import { HOME_PAGE_IMAGES } from '../../lib/homePageImages';

export const CONCIERGE_STRIP_TEST_ID = 'concierge-strip-section';

export const CONCIERGE_STRIP_KEYS = [
  'home.cta.heading',
  'home.cta.subtitle',
  'home.cta.browse_homes',
  'home.cta.compare_locations',
  'home.cta.note',
];

export const CONCIERGE_STRIP_ROUTES = {
  browseHomes: '/listings',
  compareLocations: '/location-match',
};

export default function ConciergeStrip() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { theme } = useSeason();

  return (
    <section
      className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-amber-50 to-stone-100 border-y border-amber-100"
      data-testid={CONCIERGE_STRIP_TEST_ID}
    >
      <div className="max-w-5xl mx-auto text-center">
        <div className="mb-6">
          <Home className="w-16 h-16 mx-auto text-amber-600" aria-hidden />
        </div>

        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
          {t('home.cta.heading', 'HOMES FIRST. ANSWERS WHEN YOU NEED THEM.')}
        </h2>

        <p className="text-slate-600 text-lg mb-8 max-w-3xl mx-auto">
          {t(
            'home.cta.subtitle',
            'Browse real homes, compare how each location fits your move, and lean on our relocation tools whenever a question comes up.',
          )}
        </p>

        <img
          src={HOME_PAGE_IMAGES.townhouse.src}
          alt={t(HOME_PAGE_IMAGES.townhouse.altKey, 'Row of residential townhouses in daylight')}
          className="mx-auto max-w-lg w-full h-48 object-cover rounded-xl mb-8"
          loading="lazy"
        />

        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <Button
            onClick={() => navigate(CONCIERGE_STRIP_ROUTES.browseHomes)}
            className="text-white rounded-lg px-8 py-6 text-lg transition-colors shadow-lg"
            style={{ backgroundColor: theme.colors.primary }}
            data-testid="cta-listings"
          >
            <Home className="w-5 h-5 mr-2" aria-hidden />
            {t('home.cta.browse_homes', 'Browse Homes')}
          </Button>
          <Button
            onClick={() => navigate(CONCIERGE_STRIP_ROUTES.compareLocations)}
            variant="outline"
            className="border-amber-200 text-slate-700 hover:bg-amber-100/60 rounded-lg px-8 py-6 text-lg"
            data-testid="cta-location-match"
          >
            <Sparkles className="w-5 h-5 mr-2" aria-hidden />
            {t('home.cta.compare_locations', 'Compare Locations')}
          </Button>
        </div>

        <p className="mt-5 text-sm text-slate-500">
          {t(
            'home.cta.note',
            'PCS Buddy and our location tools are here whenever you want a hand — no pressure, no gimmicks.',
          )}
        </p>
      </div>
    </section>
  );
}
